import React, { useState, useEffect, useContext } from "react";
import createAuth0Client from "@auth0/auth0-spa-js";
import appRoutes from "../components/Navigation/appRoutes";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);
const DEFAULT_LOGOUT_URL = `${window.location.origin}${appRoutes.loggedOut}`;

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onError,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth0 = async () => {
      try {
        const auth0FromHook = await createAuth0Client(initOptions);
        setAuth0(auth0FromHook);

        if (window.location.search.includes("code=")) {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          onRedirectCallback(appState);
        }

        const isAuthenticated = await auth0FromHook.isAuthenticated();

        setIsAuthenticated(isAuthenticated);

        if (isAuthenticated) {
          const user = await auth0FromHook.getUser();
          setUser(user);
        }

        setLoading(false);
      } catch (error) {
        raiseError(error);
      }
    };

    initAuth0();
  }, []); // eslint-disable-line

  const raiseError = (error) => {
    if (onError && typeof onError === "function") {
      onError(error);
    }
  };

  const handleRedirectCallback = async () => {
    try {
      setLoading(true);
      await auth0Client.handleRedirectCallback();
      const user = await auth0Client.getUser();
      setIsAuthenticated(true);
      setUser(user);
    } catch (error) {
      raiseError(error);
    }
    setLoading(false);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: async (...p) => {
          return await auth0Client.getTokenSilently(...p);
        },
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: (
          returnTo = DEFAULT_LOGOUT_URL,
          federated = initOptions?.federatedLogout === true
        ) => {
          auth0Client.logout({
            returnTo,
            federated,
          });
        },
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
